import { useCallback, useEffect, useMemo, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { debounce, get } from "radash"

import {
  claimDriverCancelHandling,
  isActiveInTripState,
} from "../utils/ride-driver-cancel"

interface UseRideStateEffectsOptions {
  rideStatus?: any
  rideSession?: any
  ratingTrips?: any
  engagement?: any
  currentState: string
  sseData: any | null
  setCurrentState: (state: any) => void
  setEngagement: (engagement: any, reset?: boolean) => void
  setAllowFetchRideSession: (allow: boolean) => void
  resetRideState: () => void
  getRideSummary: (engagementId: string) => Promise<any>
  refetchRideStatus: () => Promise<any>
  navigate: (path: string, options?: any) => void
  modal: {
    show: (options: any) => void
    close: () => void
  }
  t: (key: string) => string
  handleVibrate: () => void
  homeSessionsV2QueryKey: { all: any }
  BeAutoAssigningDriver: {
    RIDE_ACCEPTED: number
    NO_DRIVERS_AVAILABLE: number
    ASSIGNING_DRIVER: number
  }
  BEAutoRideStatus: {
    rideCancelledByDriver: number
    started: number
  }
  SSERideStatusFlag: {
    CANCELLED_BY_DRIVER: number
    FINISHED: number
  }
}

export const useRideStateEffects = ({
  rideStatus,
  rideSession,
  ratingTrips,
  engagement,
  currentState,
  sseData,
  setCurrentState,
  setEngagement,
  setAllowFetchRideSession,
  resetRideState,
  getRideSummary,
  refetchRideStatus,
  navigate,
  modal,
  t,
  handleVibrate,
  homeSessionsV2QueryKey,
  BeAutoAssigningDriver,
  BEAutoRideStatus,
  SSERideStatusFlag,
}: UseRideStateEffectsOptions) => {
  const queryClient = useQueryClient()
  const isProcessingStateChange = useRef(false)
  const handledDriverCancelIdsRef = useRef<Set<string>>(new Set())
  const handledSessionlessCancelRef = useRef(false)

  const debouncedInvalidate = useMemo(
    () =>
      debounce({ delay: 300 }, () => {
        queryClient.invalidateQueries({
          queryKey: homeSessionsV2QueryKey.all,
        })
      }),
    [queryClient, homeSessionsV2QueryKey.all]
  )

  const handleDriverCancelledByDriver = useCallback(
    (options?: {
      engagementId?: unknown
      title?: string
      message?: string
    }) => {
      const engagementId = options?.engagementId ?? engagement?.engagement_id
      const claimedId = claimDriverCancelHandling(
        handledDriverCancelIdsRef.current,
        engagementId
      )

      if (!claimedId) {
        if (handledSessionlessCancelRef.current) return false
        if (engagementId != null) return false
        handledSessionlessCancelRef.current = true
      }

      queryClient.invalidateQueries({
        queryKey: homeSessionsV2QueryKey.all,
      })
      queryClient.removeQueries({
        queryKey: ["current-user-sessions"],
      })

      setAllowFetchRideSession(false)
      resetRideState()

      navigate("/ride/booking", { replace: true })
      modal.show({
        title: options?.title ?? t("ride.trip_status.order_cancelled"),
        message: options?.message ?? t("ride.trip_status.cancelled_by_driver"),
        primaryLabel: t("common.ok"),
        onPrimaryAction: () => modal.close(),
      })

      return true
    },
    [
      engagement?.engagement_id,
      homeSessionsV2QueryKey.all,
      modal,
      navigate,
      queryClient,
      resetRideState,
      setAllowFetchRideSession,
      t,
    ]
  )

  useEffect(() => {
    if (currentState !== "finding-driver" || isProcessingStateChange.current) {
      return
    }

    if (rideStatus?.flag === BeAutoAssigningDriver.RIDE_ACCEPTED) {
      isProcessingStateChange.current = true
      setCurrentState("driver-found")
      setEngagement(rideStatus)
      handleVibrate()
      setTimeout(() => {
        isProcessingStateChange.current = false
      }, 100)
    }

    if (rideStatus?.flag === BeAutoAssigningDriver.NO_DRIVERS_AVAILABLE) {
      isProcessingStateChange.current = true
      queryClient.invalidateQueries({
        queryKey: homeSessionsV2QueryKey.all,
      })

      navigate("/ride/booking", { replace: true })
      handleVibrate()
      modal.show({
        title: t("ride.in_trip.status.no_drivers_title"),
        message: t("ride.in_trip.status.no_drivers_description"),
        primaryLabel: t("common.ok"),
        onPrimaryAction: () => modal.close(),
      })
      setTimeout(() => {
        isProcessingStateChange.current = false
      }, 500)
    }
  }, [
    rideStatus?.flag,
    currentState,
    BeAutoAssigningDriver.RIDE_ACCEPTED,
    BeAutoAssigningDriver.NO_DRIVERS_AVAILABLE,
    handleVibrate,
    homeSessionsV2QueryKey.all,
    modal,
    navigate,
    queryClient,
    rideStatus,
    setCurrentState,
    setEngagement,
    t,
  ])

  useEffect(() => {
    const engagementId = engagement?.engagement_id
    if (!engagementId || !isActiveInTripState(currentState)) return

    const newEngagement = rideSession?.last_engagement_info?.filter(
      (entry: any) => +entry.engagement_id === +engagementId
    )?.[0]

    if (rideSession?.flag === 132 && newEngagement) {
      setEngagement(newEngagement)
    }

    if (newEngagement?.status === BEAutoRideStatus.rideCancelledByDriver) {
      handleDriverCancelledByDriver({
        engagementId: newEngagement.engagement_id,
      })
      return
    }

    if (newEngagement) return

    let cancelled = false

    ;(async () => {
      try {
        const rideSummary = await getRideSummary(`${engagementId}`)

        if (cancelled) return

        if (rideSummary.status === BEAutoRideStatus.rideCancelledByDriver) {
          handleDriverCancelledByDriver({ engagementId })
        }
      } catch (error) {
        console.error("Intrip.getRideSummary", error)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    BEAutoRideStatus.rideCancelledByDriver,
    currentState,
    engagement?.engagement_id,
    getRideSummary,
    handleDriverCancelledByDriver,
    rideSession,
    setEngagement,
  ])

  useEffect(() => {
    const sessionEngagement = rideSession?.last_engagement_info?.[0]
    if (
      sessionEngagement?.status === BEAutoRideStatus.started &&
      currentState === "driver-found"
    ) {
      setCurrentState("driver-arrived")
    }

    if (
      sessionEngagement?.status === BEAutoRideStatus.started &&
      currentState === "driver-arrived"
    ) {
      setCurrentState("ride-started")
    }
  }, [rideSession, currentState, BEAutoRideStatus.started, setCurrentState])

  useEffect(() => {
    if (rideSession?.flag === 134) {
      const lastRideEngagement = get(rideSession, "last_ride.engagement_id")

      if (lastRideEngagement === engagement?.engagement_id) {
        navigate("/ride/finish", { replace: true })
        return
      }

      const filteredTrips =
        ratingTrips?.ratings?.filter((rating: any) => rating.type === 1) || []

      if (filteredTrips.length === 0) {
        return
      }

      const hasThisEngagement = filteredTrips.some(
        (rating: any) => rating?.engagement_id === engagement?.engagement_id
      )
      if (!hasThisEngagement) return

      queryClient.invalidateQueries({
        queryKey: homeSessionsV2QueryKey.all,
      })

      setAllowFetchRideSession(false)
      navigate("/ride/finish", { replace: true })
    }
  }, [
    rideSession,
    engagement?.engagement_id,
    ratingTrips?.ratings,
    homeSessionsV2QueryKey.all,
    navigate,
    queryClient,
    setAllowFetchRideSession,
  ])

  useEffect(() => {
    if (!sseData) return

    debouncedInvalidate()

    if (sseData?.data.flag === SSERideStatusFlag.CANCELLED_BY_DRIVER) {
      handleVibrate()
      ;(async () => {
        try {
          const currentRequestRideStatus = await refetchRideStatus()

          if (
            currentRequestRideStatus?.data.flag ===
            BeAutoAssigningDriver.ASSIGNING_DRIVER
          ) {
            queryClient.removeQueries({
              queryKey: ["current-user-sessions"],
            })

            setCurrentState("finding-driver")
            return
          }
        } catch (error) {
          console.log("driverCancel: refetchRequestRideStatusError SSE", error)
        }

        handleDriverCancelledByDriver({
          engagementId: engagement?.engagement_id,
          title: sseData?.data.title,
          message: sseData?.data.message,
        })
      })()
    }

    if (sseData?.data.flag === SSERideStatusFlag.FINISHED) {
      setAllowFetchRideSession(false)
      navigate("/ride/finish", { replace: true })
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: homeSessionsV2QueryKey.all,
        })
      }, 2000)
    }
  }, [
    sseData,
    BeAutoAssigningDriver.ASSIGNING_DRIVER,
    SSERideStatusFlag.CANCELLED_BY_DRIVER,
    SSERideStatusFlag.FINISHED,
    debouncedInvalidate,
    engagement?.engagement_id,
    handleDriverCancelledByDriver,
    handleVibrate,
    homeSessionsV2QueryKey.all,
    navigate,
    queryClient,
    refetchRideStatus,
    setAllowFetchRideSession,
    setCurrentState,
  ])
}
